import { AudioPlayer } from '@/components/learning/audio-player'
import { FlashcardViewer } from '@/components/learning/flashcard-viewer'
import { useKeyboardShortcuts } from '@/components/learning/focus-mode'
import { QAPanel } from '@/components/learning/qa-panel'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useModule,
  useModuleProgress,
  useTopic,
  useUpdateProgressForTopic,
} from '@/hooks/use-learning'
import { learningService } from '@/services/learning.service'
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Clock,
  Keyboard,
  Loader2,
  X,
} from 'lucide-react'
import type { RefObject, TouchEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Markdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

const HINT_KEYS_SEEN = 'pelgo:keyboard-hint-seen'
const HINT_SWIPE_SEEN = 'pelgo:swipe-hint-seen'

const SWIPE_THRESHOLD = 50

export interface ModuleLearnCoreProps {
  topicId: number
  moduleId: number
  onModuleChange: (moduleId: number) => void
  /** Scroll container for reading progress, scroll-save, and Q&A context */
  scrollContainerRef: RefObject<HTMLDivElement | null>
  /**
   * When true, hides standalone chrome (back to topic, top module segments).
   * Used on the unified topic page with a module dropdown.
   */
  embedded?: boolean
}

export function ModuleLearnCore({
  topicId: validTopicId,
  moduleId: validModuleId,
  onModuleChange,
  scrollContainerRef,
  embedded = false,
}: ModuleLearnCoreProps) {
  const navigate = useNavigate()
  const { data: module, isLoading: moduleLoading, isError } = useModule(validModuleId)
  const { data: topic } = useTopic(validTopicId)
  const { data: moduleProgress } = useModuleProgress(validModuleId)
  const updateProgress = useUpdateProgressForTopic(validTopicId)
  const scrollRestoredRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null)
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isSwipeNavigating, setIsSwipeNavigating] = useState(false)

  const [qaOpen, setQaOpen] = useState(false)

  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const [showKeyboardHint, setShowKeyboardHint] = useState(
    () => !embedded && !localStorage.getItem(HINT_KEYS_SEEN),
  )
  const [showSwipeHint, setShowSwipeHint] = useState(
    () => !embedded && !localStorage.getItem(HINT_SWIPE_SEEN) && 'ontouchstart' in window,
  )
  const [showCompleteConfirm, setShowCompleteConfirm] = useState(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: validModuleId triggers timer reset intentionally
  useEffect(() => {
    setElapsedSeconds(0)
    elapsedRef.current = setInterval(() => setElapsedSeconds((s) => s + 1), 1000)
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current)
    }
  }, [validModuleId])

  const getScrollPercent = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return 0
    const scrollable = el.scrollHeight - el.clientHeight
    if (scrollable <= 0) return 0
    return Math.round((el.scrollTop / scrollable) * 100)
  }, [scrollContainerRef])

  const sortedModules = useMemo(
    () => (topic?.modules ? [...topic.modules].sort((a, b) => a.order_index - b.order_index) : []),
    [topic?.modules],
  )

  const currentIndex = sortedModules.findIndex((m) => m.id === validModuleId)
  const currentModuleProgress = currentIndex >= 0 ? sortedModules[currentIndex] : null
  const prevModule = currentIndex > 0 ? sortedModules[currentIndex - 1] : null
  const nextModule =
    currentIndex < sortedModules.length - 1 ? sortedModules[currentIndex + 1] : null
  const isCompleted = currentModuleProgress?.status === 'completed'

  const saveScrollPosition = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      const el = scrollContainerRef.current
      if (!el || !Number.isFinite(validModuleId)) return
      const scrollable = el.scrollHeight - el.clientHeight
      if (scrollable <= 0) return
      const scrollPercent = Math.round((el.scrollTop / scrollable) * 100)
      learningService.updateProgress(validModuleId, {
        last_position: { type: 'text', scroll_percent: scrollPercent },
      })
    }, 1000)
  }, [validModuleId, scrollContainerRef])

  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    el.addEventListener('scroll', saveScrollPosition)
    return () => {
      el.removeEventListener('scroll', saveScrollPosition)
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [saveScrollPosition, scrollContainerRef])

  useEffect(() => {
    if (
      scrollRestoredRef.current ||
      !moduleProgress ||
      !scrollContainerRef.current ||
      !module?.text_content
    )
      return
    const pos = moduleProgress.last_position as { scroll_percent?: number } | null
    if (
      pos?.scroll_percent != null &&
      pos.scroll_percent > 0 &&
      moduleProgress.status === 'in_progress'
    ) {
      scrollRestoredRef.current = true
      const timer = setTimeout(() => {
        const el = scrollContainerRef.current
        if (!el) return
        const scrollable = el.scrollHeight - el.clientHeight
        if (scrollable <= 0) return
        const target = ((pos.scroll_percent ?? 0) / 100) * scrollable
        el.scrollTo({ top: target, behavior: 'smooth' })
      }, 100)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [moduleProgress, module?.text_content, scrollContainerRef])

  // biome-ignore lint/correctness/useExhaustiveDependencies: validModuleId triggers the reset intentionally
  useEffect(() => {
    scrollRestoredRef.current = false
  }, [validModuleId])

  const handleTouchStart = useCallback((e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0]
    if (!touch) return
    setTouchStart({ x: touch.clientX, y: touch.clientY })
    setSwipeOffset(0)
  }, [])

  const handleTouchMove = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (!touchStart) return
      const touch = e.touches[0]
      if (!touch) return
      const deltaX = touch.clientX - touchStart.x
      const deltaY = touch.clientY - touchStart.y

      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        const maxOffset = 100
        const offset = Math.max(-maxOffset, Math.min(maxOffset, deltaX))
        setSwipeOffset(offset)
      }
    },
    [touchStart],
  )

  const handleTouchEnd = useCallback(() => {
    if (!touchStart) return

    if (showSwipeHint && Math.abs(swipeOffset) > 10) {
      setShowSwipeHint(false)
      localStorage.setItem(HINT_SWIPE_SEEN, '1')
    }

    if (Math.abs(swipeOffset) > SWIPE_THRESHOLD) {
      if (swipeOffset > 0 && prevModule) {
        setIsSwipeNavigating(true)
        onModuleChange(prevModule.id)
      } else if (swipeOffset < 0 && nextModule) {
        setIsSwipeNavigating(true)
        onModuleChange(nextModule.id)
      }
    }

    setTouchStart(null)
    setSwipeOffset(0)
    setTimeout(() => setIsSwipeNavigating(false), 300)
  }, [touchStart, swipeOffset, prevModule, nextModule, onModuleChange, showSwipeHint])

  // biome-ignore lint/correctness/useExhaustiveDependencies: validModuleId triggers the reset intentionally
  useEffect(() => {
    setSwipeOffset(0)
    setTouchStart(null)
    setIsSwipeNavigating(false)
  }, [validModuleId])

  const { mutate: markProgress } = updateProgress
  useEffect(() => {
    if (Number.isFinite(validModuleId) && currentModuleProgress?.status === 'not_started') {
      markProgress({
        moduleId: validModuleId,
        data: { status: 'in_progress' },
      })
    }
  }, [validModuleId, currentModuleProgress?.status, markProgress])

  const handleMarkComplete = async () => {
    if (!Number.isFinite(validModuleId)) return
    await updateProgress.mutateAsync({
      moduleId: validModuleId,
      data: {
        status: 'completed',
        progress_percent: 100,
        time_spent_minutes: Math.max(1, Math.floor(elapsedSeconds / 60)),
      },
    })

    const completedCount = sortedModules.filter(
      (m) => m.status === 'completed' || m.id === validModuleId,
    ).length
    const isTopicComplete = completedCount >= sortedModules.length

    if (isTopicComplete) {
      navigate('/learn')
    } else if (nextModule) {
      onModuleChange(nextModule.id)
    } else {
      navigate('/learn')
    }
  }

  useKeyboardShortcuts({
    onNext: () => nextModule && onModuleChange(nextModule.id),
    onPrevious: () => prevModule && onModuleChange(prevModule.id),
    onComplete: () => !isCompleted && setShowCompleteConfirm(true),
    onToggleQA: () => setQaOpen((q) => !q),
    enabled: !qaOpen && !showCompleteConfirm,
  })

  const dismissKeyboardHint = () => {
    setShowKeyboardHint(false)
    localStorage.setItem(HINT_KEYS_SEEN, '1')
  }

  if (moduleLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-12 w-12 mb-4 text-destructive" />
        <p className="text-muted-foreground">Failed to load module. Please try again later.</p>
      </div>
    )
  }

  if (!module) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-muted-foreground">Module not found</p>
      </div>
    )
  }

  const swipeProgress = Math.abs(swipeOffset) / SWIPE_THRESHOLD
  const showLeftIndicator = swipeOffset > 20 && prevModule
  const showRightIndicator = swipeOffset < -20 && nextModule

  return (
    <>
      <div
        className={`w-full transition-transform duration-150 ease-out ${
          embedded ? 'px-0 sm:px-0' : 'max-w-3xl mx-auto px-3 sm:px-6 py-8'
        }`}
        style={{
          transform: isSwipeNavigating ? 'none' : `translateX(${swipeOffset * 0.3}px)`,
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {showLeftIndicator && (
          <div
            className="fixed left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-primary/10 text-primary transition-opacity z-10"
            style={{ opacity: Math.min(1, swipeProgress) }}
          >
            <ArrowLeft className="h-6 w-6" />
          </div>
        )}
        {showRightIndicator && (
          <div
            className="fixed right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-primary/10 text-primary transition-opacity z-10"
            style={{ opacity: Math.min(1, swipeProgress) }}
          >
            <ArrowRight className="h-6 w-6" />
          </div>
        )}

        {!embedded && (
          <div className="mb-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/learn/${validTopicId}`)}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              Back to Topic
            </Button>
            <button
              type="button"
              onClick={dismissKeyboardHint}
              className="hidden rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted sm:block"
              title="Hide keyboard shortcut hint"
            >
              <Keyboard className="h-4 w-4" />
            </button>
          </div>
        )}

        {!embedded && showKeyboardHint && (
          <div className="hidden sm:flex mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 items-center justify-between animate-in fade-in duration-300">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Keyboard className="h-4 w-4 text-primary shrink-0" />
              <span>
                <kbd className="px-1 py-0.5 rounded bg-muted border font-mono text-[10px]">j</kbd>
                {' / '}
                <kbd className="px-1 py-0.5 rounded bg-muted border font-mono text-[10px]">k</kbd>
                {' navigate, '}
                <kbd className="px-1 py-0.5 rounded bg-muted border font-mono text-[10px]">
                  Space
                </kbd>
                {' complete, '}
                <kbd className="px-1 py-0.5 rounded bg-muted border font-mono text-[10px]">?</kbd>
                {' ask'}
              </span>
            </div>
            <button
              type="button"
              onClick={dismissKeyboardHint}
              className="p-1 hover:bg-muted rounded-md transition-colors text-muted-foreground shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {!embedded && showSwipeHint && sortedModules.length > 1 && (
          <div className="mb-4 p-3 rounded-lg bg-primary/5 border border-primary/20 flex items-center justify-between sm:hidden animate-in fade-in duration-300">
            <p className="text-sm text-muted-foreground">
              Swipe left/right to navigate between modules
            </p>
            <button
              type="button"
              onClick={() => {
                setShowSwipeHint(false)
                localStorage.setItem(HINT_SWIPE_SEEN, '1')
              }}
              className="p-1 hover:bg-muted rounded-md transition-colors text-muted-foreground shrink-0"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {!embedded && sortedModules.length > 1 && (
          <div className="flex items-center gap-1 mb-4">
            {sortedModules.map((m, i) => (
              <div
                key={m.id}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= currentIndex ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}

        <div className={embedded ? 'mb-4' : 'mb-6'}>
          {!embedded && (
            <div className="text-xs text-muted-foreground mb-1">
              Module {currentIndex + 1} of {sortedModules.length}
            </div>
          )}
          <h2 className="text-xl sm:text-2xl font-bold mb-2 text-foreground">{module.title}</h2>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {module.estimated_minutes} min
            </span>
            {isCompleted && (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="h-3.5 w-3.5" />
                Completed
              </span>
            )}
          </div>
        </div>

        <div className="mb-6">
          <AudioPlayer module={module} topicId={validTopicId} />
        </div>

        {module.text_content ? (
          module.module_type === 'flashcards' ? (
            <div className="mb-8 space-y-6">
              <FlashcardViewer content={module.text_content} />
              <details className="border rounded-lg">
                <summary className="px-4 py-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                  View as text
                </summary>
                <div className="px-4 pb-4 prose prose-sm max-w-none text-sm leading-relaxed dark:prose-invert">
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {module.text_content.replace(/<!--[\s\S]*?-->/g, '')}
                  </Markdown>
                </div>
              </details>
            </div>
          ) : (
            <div className="prose prose-sm max-w-none mb-8 text-sm leading-relaxed dark:prose-invert">
              <Markdown remarkPlugins={[remarkGfm]}>
                {module.text_content.replace(/^#\s+.+\n+/, '')}
              </Markdown>
            </div>
          )
        ) : (
          <div className="rounded-lg border bg-muted/30 p-8 text-center mb-8">
            <p className="text-muted-foreground">Content coming soon.</p>
          </div>
        )}

        <div className="space-y-4 border-t border-border/60 pt-6">
          <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between sm:gap-2">
            {!isCompleted && (
              <div className="flex w-full justify-center sm:order-2 sm:w-auto sm:shrink-0">
                <Button
                  type="button"
                  size="sm"
                  onClick={handleMarkComplete}
                  disabled={updateProgress.isPending}
                  className="w-full sm:w-auto bg-blue-600 text-white hover:bg-blue-700"
                >
                  {updateProgress.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="mr-1 h-4 w-4" />
                  )}
                  Mark as complete
                </Button>
              </div>
            )}
            <div className="flex w-full items-center justify-between sm:order-1 sm:contents">
              <div className="flex min-w-0 flex-1 justify-start sm:order-1">
                {prevModule ? (
                  <Button variant="outline" size="sm" onClick={() => onModuleChange(prevModule.id)}>
                    <ArrowLeft className="mr-1 h-4 w-4" />
                    Previous
                  </Button>
                ) : (
                  <span className="inline-block min-w-[5.5rem]" aria-hidden />
                )}
              </div>
              <div className="flex min-w-0 flex-1 justify-end sm:order-3">
                {nextModule ? (
                  <Button variant="outline" size="sm" onClick={() => onModuleChange(nextModule.id)}>
                    Next
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <span className="inline-block min-w-[5.5rem]" aria-hidden />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <QAPanel
        moduleId={validModuleId}
        moduleTitle={module.title}
        getScrollPercent={getScrollPercent}
        isOpen={qaOpen}
        onToggle={() => setQaOpen((prev) => !prev)}
      />

      <Dialog open={showCompleteConfirm} onOpenChange={setShowCompleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark module as complete?</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark this module as complete?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompleteConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowCompleteConfirm(false)
                handleMarkComplete()
              }}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              Complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
